import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { YoolTable, MonoText } from '../../../components/ui/YoolTable';
import { ArrowLeft, Sparkles, Upload, CheckCircle } from 'lucide-react';
import SectionHeader from '../../../components/ui/fancy/SectionHeader';
import ErrorMessage from '../../../components/ErrorMessage';

const formatClp = (v) =>
  v != null ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v) : '—';

export default function FacturaUpload() {
  const { companiaId, periodo } = useParams();
  const navigate = useNavigate();

  const [emailHtml, setEmailHtml] = useState('');
  const [parsedItems, setParsedItems] = useState([]);
  const [parsedHeader, setParsedHeader] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleParsear = async () => {
    if (!emailHtml.trim()) return;
    setLoading(true);
    setError('');
    setParsedItems([]);
    try {
      const { data } = await api.post('/api/conciliacion/parsear-factura', { emailHtml });
      setParsedItems(data.items || []);
      setParsedHeader(data.factura || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCargar = async () => {
    if (!parsedItems.length) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/api/conciliacion/cargar-facturados', {
        companiaId,
        periodo,
        factura: parsedHeader,
        items: parsedItems,
      });
      setSuccess(`${data.insertados} movimientos facturados cargados correctamente.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <SectionHeader
        title="Cargar Factura"
        subtitle={`${companiaId} — Período ${periodo}`}
        action={
          <Button variant="ghost" onClick={() => navigate('/payments/conciliacion')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Paso 1 — Pegar el HTML del email de la aseguradora</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Abre el email de la aseguradora en tu cliente de correo, selecciona "Ver fuente" o "Inspeccionar",
            copia el HTML completo del cuerpo y pégalo aquí.
          </p>
          <textarea
            placeholder="Pegar aquí el HTML del email..."
            className="w-full font-mono text-xs h-48 border rounded-md p-3 resize-y focus:outline-none focus:ring-2 focus:ring-ring"
            value={emailHtml}
            onChange={e => setEmailHtml(e.target.value)}
          />
          <Button onClick={handleParsear} disabled={!emailHtml.trim() || loading}>
            <Sparkles className={`h-4 w-4 mr-2 ${loading ? 'animate-pulse' : ''}`} />
            {loading ? 'Parseando con IA...' : 'Parsear con IA'}
          </Button>
        </CardContent>
      </Card>

      {error && <ErrorMessage message={error} />}

      {parsedItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Paso 2 — Revisar y confirmar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {parsedHeader && (
              <div className="text-sm bg-muted rounded p-3 space-y-1">
                <p><strong>Factura N°{parsedHeader.numero}</strong> — {parsedHeader.fecha}</p>
                <p>Emisor: {parsedHeader.emisor_nombre} ({parsedHeader.emisor_rut})</p>
                <p>
                  Total: {formatClp(parsedHeader.total)} (Neto: {formatClp(parsedHeader.total_neto)} + IVA: {formatClp(parsedHeader.total_iva)})
                </p>
              </div>
            )}

            <p className="text-sm">
              <strong>{parsedItems.length}</strong> asegurados detectados. Revisa los datos antes de cargar.
            </p>

            <YoolTable
              columns={[
                { key: 'nombre', label: 'Nombre', sortable: true },
                { key: 'rut',    label: 'RUT',    render: item => <MonoText>{item.rut}</MonoText> },
                { key: 'numero_contrato_aseguradora', label: 'Nº Contrato',
                  render: item => <MonoText>{item.numero_contrato_aseguradora}</MonoText> },
                { key: 'tipo_seguro',  label: 'Tipo Seguro' },
                { key: 'monto_neto',   label: 'Neto CLP',   align: 'right', numeric: true, sortable: true,
                  render: item => <MonoText>{formatClp(item.monto_neto)}</MonoText> },
                { key: 'monto_total',  label: 'Total CLP',  align: 'right', numeric: true, sortable: true,
                  render: item => <MonoText>{formatClp(item.monto_total)}</MonoText> },
              ]}
              rows={parsedItems}
              variant="plain"
            />

            {success ? (
              <div className="flex items-center gap-2 text-[var(--yool-success)]">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">{success}</span>
              </div>
            ) : (
              <Button onClick={handleCargar} disabled={saving}>
                <Upload className={`h-4 w-4 mr-2 ${saving ? 'animate-pulse' : ''}`} />
                {saving ? 'Cargando...' : 'Confirmar y cargar'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
